"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { stripeAPI } from "../config/api"

const Admin = () => {
  const [password, setPassword] = useState("")
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [stripePayments, setStripePayments] = useState([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState("")

  const ADMIN_PASSWORD = "leafwell2024"

  useEffect(() => {
    // Check if already authenticated
    const authStatus = localStorage.getItem("adminAuthenticated")
    if (authStatus === "true") {
      setIsAuthenticated(true)
      loadStripePayments()
    }
  }, [])

  const handleLogin = (e) => {
    e.preventDefault()
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true)
      localStorage.setItem("adminAuthenticated", "true")
      loadStripePayments()
      setMessage("Welcome to Admin Dashboard!")
    } else {
      setMessage("Invalid password")
    }
  }

  const handleLogout = () => {
    setIsAuthenticated(false)
    localStorage.removeItem("adminAuthenticated")
    setPassword("")
    setMessage("Logged out successfully")
  }

  // Test backend connection
 

  const loadStripePayments = async () => {
    setLoading(true)
    try {
      console.log("🔍 Fetching payments from Stripe API...")
      
      const data = await stripeAPI.getPayments()
      
      setStripePayments(data.payments || [])
      setMessage(`Loaded ${data.payments?.length || 0} Stripe payments`)
      console.log("✅ Payments loaded successfully")
    } catch (error) {
      console.error("❌ Error loading Stripe payments:", error)
      setMessage(`Failed to load payments: ${error.message}`)
      setStripePayments([])
    }
    setLoading(false)
  }

  const exportToCSV = (data, filename) => {
    if (data.length === 0) {
      setMessage("No data to export")
      return
    }

    let csvContent = ""
    // CSV headers for Stripe payments
    csvContent =
      "Payment ID,Date,Customer Name,Email,Phone,Amount,Status,Event,Event Date,Guests,Card,Special Requests,Add-ons\n"

    // CSV data for Stripe payments
    data.forEach((payment) => {
      const row = [
        payment.id,
        new Date(payment.created * 1000).toLocaleDateString(),
        payment.customer?.name || "N/A",
        payment.customer?.email || "N/A",
        payment.metadata?.customer_phone || "N/A",
        `$${(payment.amount / 100).toFixed(2)}`,
        payment.status,
        payment.metadata?.event_name || "N/A",
        payment.metadata?.event_date || "N/A",
        payment.metadata?.guests || "N/A",
        `${payment.payment_method?.card?.brand?.toUpperCase()} ****${payment.payment_method?.card?.last4}` || "N/A",
        payment.metadata?.special_requests || "None",
        payment.metadata?.selected_upsells || "None",
      ]
        .map((field) => `"${field}"`)
        .join(",")
      csvContent += row + "\n"
    })

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    const url = URL.createObjectURL(blob)
    link.setAttribute("href", url)
    link.setAttribute("download", filename)
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    setMessage(`${filename} exported successfully!`)
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount / 100) // Stripe amounts are in cents
  }

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getTotalRevenue = () => {
    return stripePayments.reduce((total, payment) => total + payment.amount, 0)
  }

  const getRevenueThisMonth = () => {
    const currentMonth = new Date().getMonth()
    const currentYear = new Date().getFullYear()
    return stripePayments
      .filter((payment) => {
        const paymentDate = new Date(payment.created * 1000)
        return paymentDate.getMonth() === currentMonth && paymentDate.getFullYear() === currentYear
      })
      .reduce((total, payment) => total + payment.amount, 0)
  }

  const getSuccessfulPayments = () => {
    return stripePayments.filter((payment) => payment.status === "succeeded")
  }



  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">🔐 Admin Login</h1>
            <p className="text-gray-600">Enter password to access dashboard</p>
          </div>
          {message && (
            <div
              className={`mb-4 p-3 rounded-lg text-center ${
                message.includes("Invalid") ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"
              }`}
            >
              {message}
            </div>
          )}
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition duration-300"
                placeholder="Enter admin password"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-6 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition duration-300"
            >
              Login to Dashboard
            </button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/" className="text-green-600 hover:text-green-700 text-sm">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-xl font-bold text-gray-900">
                🌿 Leafwell Farm
              </Link>
              <span className="text-gray-500">|</span>
              <h1 className="text-lg font-semibold text-gray-700">Admin Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={loadStripePayments}
                className="bg-blue-100 text-blue-800 px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-200 transition duration-300"
              >
                🔄 Refresh Payments
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-100 text-red-800 px-3 py-2 rounded-lg text-sm font-medium hover:bg-red-200 transition duration-300"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Message Display */}
        {message && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-blue-800">{message}</p>
          </div>
        )}

      

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(getTotalRevenue())}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(getRevenueThisMonth())}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Successful Payments</p>
                <p className="text-2xl font-bold text-gray-900">{getSuccessfulPayments().length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg. Payment</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stripePayments.length > 0 ? formatCurrency(getTotalRevenue() / stripePayments.length) : "$0.00"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stripe Payments Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="px-6 py-4 bg-gray-50 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">Stripe Payments ({stripePayments.length})</h2>
              <div className="flex space-x-3">
                <button
                  onClick={() =>
                    exportToCSV(
                      stripePayments,
                      `leafwell-stripe-payments-${new Date().toISOString().split("T")[0]}.csv`,
                    )
                  }
                  className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition duration-300"
                >
                  📊 Export CSV
                </button>
              </div>
            </div>
          </div>
          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                <p className="mt-2 text-gray-600">Loading Stripe payments...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {stripePayments.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No Stripe payments found</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Payments will appear here once customers complete bookings
                    </p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Payment ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Event Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Payment Method
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {stripePayments.map((payment) => (
                        <tr key={payment.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{payment.id}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{payment.customer?.name || "N/A"}</div>
                              <div className="text-sm text-gray-500">{payment.customer?.email || "N/A"}</div>
                              <div className="text-sm text-gray-500">{payment.metadata?.customer_phone || "N/A"}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {payment.metadata?.event_name || "N/A"}
                              </div>
                              <div className="text-sm text-gray-500">{payment.metadata?.event_date || "N/A"}</div>
                              <div className="text-sm text-gray-500">{payment.metadata?.guests || "N/A"} guests</div>
                              {payment.metadata?.selected_upsells && payment.metadata.selected_upsells !== "None" && (
                                <div className="text-xs text-blue-600">
                                  Add-ons: {payment.metadata.selected_upsells}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatCurrency(payment.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                payment.status === "succeeded"
                                  ? "bg-green-100 text-green-800"
                                  : payment.status === "pending"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-red-100 text-red-800"
                              }`}
                            >
                              {payment.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(payment.created)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {payment.payment_method?.card ? (
                              <div>
                                <div className="font-medium">
                                  {payment.payment_method.card.brand?.toUpperCase()} ****
                                  {payment.payment_method.card.last4}
                                </div>
                              </div>
                            ) : (
                              "N/A"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Admin
